import {
  ChangeLogDto,
  MemberChangeLogViewDto,
} from '@act/shared/data-transfer-objects';
import {
  PayloadAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { changeLogRequests } from '../requests/change-log.requests';
import { useAppSelector } from '../store-hooks';
import { ChangeLogState } from '../types/changeLogTypes';
import { createApiThunk } from '../utils/api-thunk';
import { resetRedux } from '../actions/shared-actions';

// THUNKS/ACTIONS ==============================================================
export const getMemberChangeLogs = createApiThunk(
  changeLogRequests.getMemberChangeLogs
);

// INITIAL STATE ===============================================================
const changeLogAdapter = createEntityAdapter<ChangeLogDto>();
const initialState: ChangeLogState = {
  status: 'initial',
  changeLogs: changeLogAdapter.getInitialState(),
  memberChangeLogIds: {},
};

// SLICE =======================================================================
export const changeLogSlice = createSlice({
  name: 'changeLogs',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getMemberChangeLogs.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        getMemberChangeLogs.fulfilled,
        (state, action: PayloadAction<MemberChangeLogViewDto>) => {
          const { memberId, memberChangeLogIds, changeLogs } = action.payload;
          changeLogAdapter.addMany(state.changeLogs, changeLogs);
          state.memberChangeLogIds[memberId] = memberChangeLogIds;
          state.status = 'success';
        }
      )
      .addCase(getMemberChangeLogs.rejected, state => {
        state.status = 'failed';
      });
  },
});

// REDUCER =====================================================================
export const changeLogReducer = changeLogSlice.reducer;

// SELECTORS ===================================================================
const selectState = (state): ChangeLogState =>
  (state as { changeLogs: ChangeLogState })?.changeLogs;

const selectChangeLogAdapterState = state =>
  (state as { changeLogs: ChangeLogState })?.changeLogs?.changeLogs;

const selectChangeLogSliceStatus = () =>
  createSelector(selectState, state => state.status);

const selectMemberChangeLogsByMember = (memberId: string) =>
  createSelector(
    selectState,
    selectChangeLogAdapterState,
    (state, adapterState) => {
      return state.memberChangeLogIds[memberId].map(
        i => adapterState.entities[i]
      );
    }
  );

// EXPORT HELPER ===============================================================
export const changeLogAppSelectors = {
  useSelectChangeLogSliceStatus: (): string =>
    useAppSelector(selectChangeLogSliceStatus()),
  useSelectMemberChangeLogsByMember: (memberId: string): ChangeLogDto[] =>
    useAppSelector(selectMemberChangeLogsByMember(memberId)),
};

// EFFECTS =====================================================================
// WEBSOCKET ACTIONS ===========================================================
